/**
 * The row menu of a ClimGrid (masterplan credit-limits-v3, package A4; E15: right-click and the menu key open open
 * invoices, open orders, sales, comments and the mail to the salesperson; the entries come from the page, A5).
 *
 * Built on `ui/ContextMenu`, no Radix (the app has none): it is a `role="menu"`, focus moves to the first enabled
 * item, ArrowUp/ArrowDown/Home/End move, Escape closes on the app's one Escape stack and focus returns to the row that
 * opened it. Opening follows inventory-app `web/src/components/table/AnalysisTable.tsx` (read 2026-10-22): right-click
 * at the pointer, the menu key and Shift+F10 below the row's name, where a pointer would have been.
 * The menu belongs to a row, not to a position: ClimGrid remembers the row's KEY (`menuRowOf`), so new data that moves
 * the row keeps the menu on it, and new data without it closes the menu.
 */
import { ContextMenu, type ContextMenuItem } from '../../ui/ContextMenu';

export type ClimMenuItem = ContextMenuItem;

/** Where the menu opens and for which row. */
export interface ClimMenuAnchor { rowKey: string; x: number; y: number }

/** The keys that open a row menu from the keyboard: the menu key and Shift+F10. */
export function isMenuKey(e: { key: string; shiftKey: boolean }): boolean {
    return e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10');
}

/**
 * The point a keyboard-opened menu takes: below the row's name, 48 px in. A row without a size (not laid out) gives
 * (0, 0), which `ContextMenu` reads as «anchor under the focused element», the row itself.
 */
export function keyboardMenuPoint(rect: { left: number; bottom: number; width: number; height: number }): { x: number; y: number } {
    if (rect.width === 0 && rect.height === 0) return { x: 0, y: 0 };
    return { x: rect.left + 48, y: rect.bottom - 4 };
}

interface Props {
    anchor: ClimMenuAnchor;
    items: ClimMenuItem[];
    onClose: () => void;
}

export function ClimRowMenu({ anchor, items, onClose }: Props) {
    return <ContextMenu x={anchor.x} y={anchor.y} items={items} onClose={onClose} />;
}
